/**
 * Adaptador de reconocimiento de voz del navegador (FR-VOICE-1..12, spec/05-security.md §4.3).
 *
 * Principios y garantias de seguridad:
 *  - Divulgacion previa obligatoria sobre si el motor del navegador es local o remoto
 *    antes de solicitar el permiso de microfono (spec/05-security.md §11).
 *  - Cero persistencia de audio: ningun artefacto de audio se guarda en disco ni
 *    en almacenamiento local (FR-VOICE-6).
 *  - Si el navegador no ofrece reconocimiento, se declara explicitamente y se ofrece
 *    la ruta manual sin degradar el resto de la app (FR-VOICE-12, FR-VOICE-7).
 */

export interface SpeechRecognitionResultPayload {
  readonly transcript: string;
  readonly isFinal: boolean;
}

export interface SpeechRecognitionCallbacks {
  readonly onTranscript: (payload: SpeechRecognitionResultPayload) => void;
  readonly onError: (error: string) => void;
  readonly onEnd: () => void;
}

interface WebkitSpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionConstructor = new () => WebkitSpeechRecognitionInstance;

export function isSpeechRecognitionSupported(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return Boolean(w.SpeechRecognition || w.webkitSpeechRecognition);
}

/**
 * Informacion sobre el motor de voz disponible en el navegador.
 */
export function getSpeechEngineInfo(): {
  supported: boolean;
  isLikelyRemote: boolean;
  description: string;
} {
  const supported = isSpeechRecognitionSupported();
  if (!supported) {
    return {
      supported: false,
      isLikelyRemote: false,
      description:
        "Este navegador no cuenta con la API SpeechRecognition nativa. La ruta manual está 100% disponible.",
    };
  }

  // La mayoria de navegadores basados en Chromium / WebKit procesan el habla en servidores de Google/Apple
  return {
    supported: true,
    isLikelyRemote: true,
    description:
      "El reconocimiento se realiza a través del motor de voz de tu navegador. El audio es efímero durante la captura y nunca es almacenado por FinVoz.",
  };
}

export class BrowserSpeechRecognizer {
  private recognition: WebkitSpeechRecognitionInstance | null = null;
  private isListening = false;

  constructor(private readonly callbacks: SpeechRecognitionCallbacks) {}

  get listening(): boolean {
    return this.isListening;
  }

  start(lang = "es-ES"): boolean {
    if (this.isListening) return true;

    const w = window as unknown as {
      SpeechRecognition?: SpeechRecognitionConstructor;
      webkitSpeechRecognition?: SpeechRecognitionConstructor;
    };
    const RecognitionClass = w.SpeechRecognition || w.webkitSpeechRecognition;

    if (!RecognitionClass) {
      this.callbacks.onError(
        "El navegador no soporta reconocimiento de voz. Usa la ruta de entrada manual.",
      );
      return false;
    }

    try {
      const rec = new RecognitionClass();
      rec.continuous = false;
      rec.interimResults = true;
      rec.lang = lang;

      rec.onresult = (event: unknown) => {
        const e = event as {
          resultIndex: number;
          results: Array<Array<{ transcript: string }> & { isFinal: boolean }>;
        };
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const item = e.results[i];
          if (item && item[0]) {
            this.callbacks.onTranscript({
              transcript: item[0].transcript,
              isFinal: item.isFinal,
            });
          }
        }
      };

      rec.onerror = (event: unknown) => {
        const e = event as { error?: string };
        let msg = "Error en reconocimiento de voz";
        if (e.error === "not-allowed" || e.error === "service-not-allowed") {
          msg = "Permiso de micrófono denegado. Se activará la entrada manual.";
        } else if (e.error === "no-speech") {
          msg = "No se detectó voz o hubo silencio. Se activará la entrada manual.";
        } else if (e.error === "audio-capture") {
          msg = "No se encontró ningún micrófono disponible. Se activará la entrada manual.";
        } else if (e.error === "network") {
          msg = "Error de conexión con el motor de voz. Se activará la entrada manual.";
        } else {
          msg = `Error en reconocimiento de voz: ${e.error ?? "desconocido"}. Se activará la entrada manual.`;
        }
        this.callbacks.onError(msg);
      };

      rec.onend = () => {
        this.isListening = false;
        this.callbacks.onEnd();
      };

      this.recognition = rec;
      rec.start();
      this.isListening = true;
      return true;
    } catch (err) {
      this.callbacks.onError((err as Error).message);
      return false;
    }
  }

  stop(): void {
    if (this.recognition && this.isListening) {
      this.recognition.stop();
      this.isListening = false;
    }
  }

  abort(): void {
    if (this.recognition && this.isListening) {
      this.recognition.abort();
      this.isListening = false;
    }
  }
}
